import { useCallback, useEffect, useMemo, useRef } from "react";

import { useNavigate, useSearchParams } from "@remix-run/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

// AIDEV-NOTE: Guards against string "undefined" or other non-UUID values reaching DB queries
const UUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

import { useSupabaseSession } from "~/context/SupabaseSessionContext";
import { useUploadContext } from "~/context/UploadContext";
import type {
    TPart,
    TPartInteractionWithImage,
    TSession,
    TSessionStatus,
} from "~/database/types";
import type { TClientSettings } from "~/database/types/practitioner-types";
import { supabase } from "~/integrations/supabase/ssr.browser";
import { trackEvent, trackEventAsUser } from "~/lib/analytics";
import { logger } from "~/lib/logger";
import { hydrateSessionParts } from "~/lib/session-parts-data";
// AIDEV-NOTE: Bot status codes from Recall.ai webhooks + legacy MeetingBaas, written to meetingBaasBotStatus column
type BotStatusCode =
    | "assigned"
    | "joining_call"
    | "in_waiting_room"
    | "in_call_not_recording"
    | "recording_permission_allowed"
    | "recording_permission_denied"
    | "in_call_recording"
    | "call_ended"
    | "done"
    | "fatal";
import type { PartMutationPayload } from "~/modules.server/part/mutations";
import type { GetSessionResult } from "~/modules.server/session/get-session";
import type {
    IdentifiedPart,
    SessionMutationVariables,
    SessionWithParts,
} from "~/types";

import { useApiClient } from "./use-api-client";
import type { AnalyticsEvent } from "../lib/events";

const ENCODING_DEBUG_PATTERNS = [
    "\u00e2\u20ac\u201d", // â€”
    "\u00e2\u20ac\u2122", // â€™
    "\u00e2\u20ac\u0153", // â€œ
    "\u00e2\u20ac", // â€
    "\u00c3", // Ã
];

type EncodingDebugWindow = Window & {
    __ifsEncodingDebugOnce?: Record<string, boolean>;
};

const inspectEncoding = (value: string | null | undefined) => {
    if (!value) {
        return {
            hasValue: false,
            length: 0,
            hasMojibake: false,
            firstMojibakeIndex: -1,
            firstEmDashIndex: -1,
            snippet: "",
        };
    }

    const firstMojibakeIndex = ENCODING_DEBUG_PATTERNS.reduce(
        (index, pattern) => {
            const patternIndex = value.indexOf(pattern);
            if (patternIndex === -1) return index;
            if (index === -1) return patternIndex;
            return Math.min(index, patternIndex);
        },
        -1,
    );
    const firstEmDashIndex = value.indexOf("\u2014");
    const anchor =
        firstMojibakeIndex >= 0
            ? firstMojibakeIndex
            : firstEmDashIndex >= 0
              ? firstEmDashIndex
              : 0;
    const snippetStart = Math.max(0, anchor - 40);
    const snippet = value.slice(snippetStart, snippetStart + 140);

    return {
        hasValue: true,
        length: value.length,
        hasMojibake: firstMojibakeIndex >= 0,
        firstMojibakeIndex,
        firstEmDashIndex,
        snippet,
    };
};

const logEncodingOnce = (key: string, payload: Record<string, unknown>) => {
    if (typeof window === "undefined") return;
    const debugWindow = window as EncodingDebugWindow;
    debugWindow.__ifsEncodingDebugOnce ??= {};
    if (debugWindow.__ifsEncodingDebugOnce[key]) return;
    debugWindow.__ifsEncodingDebugOnce[key] = true;
    // AIDEV-NOTE: Temporary mojibake diagnostics. Remove after encoding investigation is complete.
    logger
        .withContext({ ...payload, encodingDebugKey: key })
        .info("ENCODING_DEBUG");
};

const IDLE_STATUSES = [
    "COMPLETED",
    "ERROR_TRANSCRIBING",
    "ERROR_ANALYZING",
    "ERROR_OTHER",
    "BILLING_BLOCKED",
];

const isIdleStatus = (status?: TSessionStatus) =>
    status && IDLE_STATUSES.includes(status);

export const useSessionStatus = (sessionId: string) => {
    const { isUploading } = useUploadContext();
    const queryClient = useQueryClient();
    // AIDEV-NOTE: Track previous status to detect transitions to idle (COMPLETED/ERROR).
    // When a session finishes processing, invalidate billing + session list queries.
    const prevStatusRef = useRef<TSessionStatus | undefined>(undefined);

    // AIDEV-NOTE: Data is prefetched on server in _protected.sessions.$sessionId._index.tsx
    const {
        data: session,
        isLoading,
        refetch,
        isError,
    } = useQuery({
        queryKey: ["sessionStatus", sessionId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from("session")
                .select(
                    "status, uuid, meetingBaasBotStatus, meetingBaasBotId, recallBotId, hasResearchRelease",
                )
                .eq("uuid", sessionId)
                .maybeSingle();

            if (error) throw error;
            // AIDEV-NOTE: This is a browser-direct Supabase read, so it is
            // subject to RLS — and a CONSULTANT has no RLS access to the
            // grantor's session (their access is resolved server-side by
            // consultantAccessService, in /api/session). Without this fallback
            // the query returns null and SessionFeedbackPage renders
            // "Session Not Found" even though /api/session served the session
            // fine. Fall back to the payload the session query already holds.
            if (data) return data;

            const cached = queryClient.getQueryData<SessionCacheData>([
                "session",
                sessionId,
                null,
            ]);
            const cachedSession = cached?.session;
            if (!cachedSession) return null;
            return {
                status: cachedSession.status,
                uuid: cachedSession.uuid,
                meetingBaasBotStatus:
                    cachedSession.meetingBaasBotStatus ?? null,
                meetingBaasBotId: cachedSession.meetingBaasBotId ?? null,
                recallBotId: cachedSession.recallBotId ?? null,
                hasResearchRelease: cachedSession.hasResearchRelease ?? null,
            };
        },
        retry: false,
        staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
        initialDataUpdatedAt: 0, // Treat prefetched data as fresh
        refetchOnWindowFocus: (query) => {
            // AIDEV-NOTE: EVO-681 fix - Always refetch on window focus during non-idle states
            // Previous logic disabled refetch when isUploading=true, but this caused stale status
            // when upload completed in background (laptop closed) and events didn't fire
            const status = query.state.data?.status;
            return !isIdleStatus(status);
        },
        enabled: (query) => {
            if (!sessionId || !UUID_REGEX.test(sessionId)) {
                return false;
            }
            const status = query.state.data?.status;
            return !isIdleStatus(status);
        },
        refetchInterval: (query) => {
            // Stop polling if session is in a final state
            const status = query.state.data?.status;
            if (isIdleStatus(status)) {
                return false;
            }
            return 5000; // Continue polling every 5 seconds
        },
    });

    // AIDEV-NOTE: When session transitions to an idle state (COMPLETED, ERROR, etc.),
    // invalidate billing and session list so sidebar count + list update promptly.
    useEffect(() => {
        const currentStatus = session?.status as TSessionStatus | undefined;
        const prev = prevStatusRef.current;
        prevStatusRef.current = currentStatus;

        if (prev && !isIdleStatus(prev) && isIdleStatus(currentStatus)) {
            queryClient.invalidateQueries({
                queryKey: ["billing", "upgrade-context"],
            });
            queryClient.invalidateQueries({
                queryKey: ["practitioner", "sessions"],
            });
        }
    }, [session?.status, queryClient]);

    // AIDEV-NOTE: Memoize return value to prevent unnecessary re-renders
    return useMemo(
        () => ({
            sessionStatus: session?.status as TSessionStatus,
            hasResearchRelease: session?.hasResearchRelease ?? false,
            isLoading,
            isError,
            refetch,
            meetingBaasBotStatus: session?.meetingBaasBotStatus
                ? (session.meetingBaasBotStatus as BotStatusCode)
                : session?.meetingBaasBotId
                  ? ("assigned" as const)
                  : undefined,
            recallBotId: session?.recallBotId ?? null,
        }),
        [session, isLoading, isError, refetch],
    );
};

export interface SessionCacheData {
    session: GetSessionResult["session"];
    parts: {
        present: GetSessionResult["parts"];
        other: IdentifiedPart[];
        suspectedParts: IdentifiedPart[];
    };
    commitments: GetSessionResult["commitments"];
    effectiveSettings: TClientSettings;
    status: TSessionStatus | undefined;
    showParticipantControls: boolean;
    // AIDEV-NOTE: sessionId nullable for chat/note-based part meditations
    meditations?: Array<{
        id: string;
        sessionId: string | null;
        partInteractionId: string | null;
        // AIDEV-NOTE: Mirrors the DB enum; "exercise" rows never appear here
        // (they have sessionId null) but the row type includes it.
        type: "intro" | "session" | "path" | "part" | "exercise";
        status: "PENDING" | "IN_PROGRESS" | "COMPLETED" | "ERROR" | "CANCELLED";
        createdAt: string | null;
        updatedAt: string | null;
        signedUrl?: string;
    }>;
    hasAssessment?: boolean;
    // AIDEV-NOTE: Stamped by the /api/session consultant branch. The consultant
    // sees the SAME /consultation page as the practitioner; these two fields
    // drive the read-only chrome and tell the page whose chat to load.
    viewerRole?: "consultant";
    grantorId?: string;
    latestRecallReasonEvent?: {
        code: "call_ended" | "fatal";
        subCode: string | null;
        eventTimestamp: string;
    } | null;
}

export const useSession = (sessionId: string) => {
    const { token } = useSupabaseSession();
    const apiClient = useApiClient();
    const { isUploading } = useUploadContext();
    const [searchParams] = useSearchParams();
    const sessionToken = searchParams.get("sessionToken");

    // AIDEV-NOTE: Data is prefetched on server in _protected.sessions.$sessionId.tsx
    const { data, isLoading, refetch, isError } = useQuery<SessionCacheData>({
        queryKey: ["session", sessionId, sessionToken],
        queryFn: async () => {
            const response = await apiClient.get("/session", {
                params: {
                    sessionId,
                    sessionToken: sessionToken ?? undefined,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({
                    error: "Failed to fetch session and parse error",
                }));
                throw new Error(errorData.error || "Failed to fetch session");
            }

            const responseData = (await response.json()) as GetSessionResult;

            logEncodingOnce(`use-session-api-${sessionId}`, {
                boundary: "useSession.queryFn.responseJson",
                sessionId,
                shortSummary: inspectEncoding(
                    responseData?.session?.shortSummary,
                ),
                longSummary: inspectEncoding(
                    responseData?.session?.longSummary,
                ),
                rawSummary: inspectEncoding(
                    responseData?.session?.rawReport?.summary,
                ),
            });

            const effectiveSettings = responseData.effectiveSettings;

            const partsData = hydrateSessionParts(responseData);

            return {
                session: responseData?.session,
                parts: partsData,
                commitments: responseData?.commitments ?? [],
                effectiveSettings: effectiveSettings ?? {},
                status: responseData?.session?.status,
                showParticipantControls:
                    responseData?.showParticipantControls ?? false,
                // AIDEV-NOTE: Using flat meditations with signedUrl
                meditations: responseData.meditations,
                hasAssessment: responseData.hasAssessment ?? false,
                // AIDEV-NOTE: Consultant read path only — drives read-only chrome.
                viewerRole: (responseData as { viewerRole?: "consultant" })
                    .viewerRole,
                grantorId: (responseData as { grantorId?: string }).grantorId,
                latestRecallReasonEvent: responseData.latestRecallReasonEvent
                    ? {
                          code: responseData.latestRecallReasonEvent.code as
                              | "call_ended"
                              | "fatal",
                          subCode: responseData.latestRecallReasonEvent.subCode,
                          eventTimestamp:
                              responseData.latestRecallReasonEvent
                                  .eventTimestamp,
                      }
                    : null,
            } satisfies SessionCacheData;
        },
        retry: false,
        staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
        initialDataUpdatedAt: 0, // Treat prefetched data as fresh
        refetchOnWindowFocus: (query) => {
            // AIDEV-NOTE: EVO-681 fix - Always refetch on window focus during non-idle states
            // Previous logic disabled refetch when isUploading=true, but this caused stale status
            // when upload completed in background (laptop closed) and events didn't fire
            const status = query.state.data?.status;
            return !isIdleStatus(status);
        },
        enabled: (query) => {
            // AIDEV-NOTE: Don't fetch if sessionId is empty/invalid (guards against string "undefined")
            if (!sessionId || !UUID_REGEX.test(sessionId)) {
                return false;
            }
            const status = query.state.data?.status;
            if (!token && !sessionToken) {
                return false;
            }
            return !isIdleStatus(status);
        },
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            return isIdleStatus(status) ? false : 5000;
        },
    });

    const parts = useMemo(
        (): SessionCacheData["parts"] =>
            data?.parts || { present: [], other: [], suspectedParts: [] },
        [data?.parts],
    );

    const effectiveSettings = useMemo(
        (): TClientSettings => data?.effectiveSettings ?? {},
        [data?.effectiveSettings],
    );

    // AIDEV-NOTE: Memoize return value to prevent unnecessary re-renders
    return useMemo(
        () => ({
            session: data?.session,
            parts,
            commitments: data?.commitments ?? [],
            effectiveSettings,
            sessionToken,
            isLoading,
            refetch,
            isError,
            showParticipantControls: data?.showParticipantControls ?? false,
            meditations: data?.meditations,
            hasAssessment: data?.hasAssessment ?? false,
            // AIDEV-NOTE: Consultant viewing someone else's session. The page is
            // the SAME one the practitioner sees; these drive read-only chrome.
            isConsultantViewer: data?.viewerRole === "consultant",
            grantorId: data?.grantorId,
            latestRecallReasonEvent: data?.latestRecallReasonEvent ?? null,
        }),
        [
            data?.session,
            parts,
            data?.commitments,
            effectiveSettings,
            sessionToken,
            isLoading,
            refetch,
            isError,
            data?.showParticipantControls,
            data?.viewerRole,
            data?.grantorId,
            data?.meditations,
            data?.hasAssessment,
            data?.latestRecallReasonEvent,
        ],
    );
};

interface SourceMap {
    end: number;
    start: number;
    identifier: string;
}

interface ConversationContent {
    text: string;
    type: string;
    _promptlSourceMap?: SourceMap[];
}

interface ConversationMessage {
    role: string;
    content: ConversationContent[];
}

interface Conversation {
    conversation: ConversationMessage[];
    response: { text: string };
}

export function extractIdentifierValues(
    conversation: Conversation,
): Record<string, string> {
    const identifierValues: Record<string, string> = {};

    // Process each message in the conversation
    for (const message of conversation.conversation) {
        // Process each content item in the message
        for (const content of message.content) {
            // Check if content has source map
            if (content._promptlSourceMap) {
                // Process each source map entry
                for (const sourceMap of content._promptlSourceMap) {
                    // Extract the text using start and end positions
                    const value = content.text.substring(
                        sourceMap.start,
                        sourceMap.end,
                    );
                    // Store the value with its identifier
                    identifierValues[sourceMap.identifier] = value;
                }
            }
        }
    }

    return identifierValues;
}

// Example usage:
export function processConversation(conversation: Conversation) {
    try {
        const identifierValues = extractIdentifierValues(conversation);

        identifierValues.detail = conversation.response.text;

        return identifierValues;
    } catch (error) {
        logger.withError(error).error("Error processing conversation");
        return {};
    }
}

const topLevelFields = ["name", "type", "quote", "recap"];
/* This modifies the same session cache so it's in use-session. */
export const usePartMutation = (
    sessionId: string,
    sessionOwnerId?: string,
    sessionToken?: string | null,
) => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { userId } = useSupabaseSession();

    // AIDEV-NOTE: Mirror recipient actions under the session owner's identity.
    // Uses Record<string, unknown> to avoid complex generic inference issues with trackEvent
    const trackEventWithAttribution = useCallback(
        (event: AnalyticsEvent["name"], props: Record<string, unknown>) => {
            // Cast to satisfy trackEvent's generic signature
            (trackEvent as (e: string, p: Record<string, unknown>) => void)(
                event,
                props,
            );

            const isSessionOwner = sessionOwnerId === userId;

            if (!isSessionOwner && sessionOwnerId) {
                trackEventAsUser(event, sessionOwnerId, props, apiClient);
            }
        },
        [sessionOwnerId, userId, apiClient],
    );

    return useMutation({
        mutationFn: async (payload: PartMutationPayload) => {
            const startTime = Date.now();

            if (payload.action === "merge") {
                trackEventWithAttribution("part merge started", {
                    session: sessionId,
                    numParts: payload.mergePartIds.length,
                });

                const promise = apiClient.post("/part/mutations", payload);
                toast.promise(promise, {
                    position: "bottom-center",
                    loading: "Merging parts... this will take 10-20 seconds.",
                    success: "Parts merged successfully",
                    error: "Failed to merge parts",
                });

                const resp = await promise;
                const data = await resp.json();

                trackEventWithAttribution("part merge completed", {
                    session: sessionId,
                    numParts: payload.mergePartIds.length,
                    duration: Math.round((Date.now() - startTime) / 1000),
                });

                return data;
            }

            if (payload.action === "split") {
                trackEventWithAttribution("part split started", {
                    session: sessionId,
                });

                const promise = apiClient
                    .post("/part/mutations", payload)
                    .then(async (resp) => {
                        const data = await resp.json();
                        if (!resp.ok) {
                            throw new Error(
                                data?.error || "Failed to split part",
                            );
                        }
                        return data;
                    });
                toast.promise(promise, {
                    position: "bottom-center",
                    style: {
                        backgroundColor: "rgb(255, 255, 255)",
                        color: "rgb(0, 0, 0)",
                    },
                    loading: "Splitting part... this will take 10-20 seconds.",
                    success: "Part split successfully",
                    error: (error) =>
                        error instanceof Error
                            ? error.message
                            : "Failed to split part",
                });

                const data = await promise;

                trackEventWithAttribution("part split completed", {
                    session: sessionId,
                    duration: Math.round((Date.now() - startTime) / 1000),
                });

                return data;
            }

            // For simpler operations (rename, delete), just make the request
            const resp = await apiClient.post("/part/mutations", payload);
            const data = await resp.json();

            return data;
        },
        onMutate: async (variables) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({
                queryKey: ["session", sessionId, sessionToken],
            });

            // Snapshot the previous value
            const previousData = queryClient.getQueryData([
                "session",
                sessionId,
                sessionToken,
            ]) as SessionWithParts | null;

            if (variables.action === "split" || variables.action === "merge") {
                queryClient.invalidateQueries({
                    queryKey: ["session", sessionId, sessionToken],
                });
                return { previousData };
            }

            // Variables to store data for logging
            let targetPart: TPartInteractionWithImage | null = null;
            let updatedData: SessionWithParts | null = null;

            // Optimistically update the cache with new values
            queryClient.setQueryData(
                ["session", sessionId, sessionToken],
                (old: SessionWithParts | null) => {
                    if (!old || !previousData) {
                        return old;
                    }

                    let updatedPart: TPartInteractionWithImage | null = null;

                    switch (variables.action) {
                        case "rename-interaction": {
                            // AIDEV-NOTE: EVO-1697 — gear-icon rename updates this
                            // interaction's name AND the canonical part.name. Only
                            // the targeted interaction's `name` changes; sibling
                            // interactions of the same canonical part keep their
                            // own names but reflect the updated nested `part.name`.
                            const renamed = old.parts.present.find(
                                (p) => p.id === variables.partId,
                            );
                            const renamedCanonicalId = renamed?.partId;
                            return {
                                ...old,
                                parts: {
                                    ...old.parts,
                                    present: old.parts.present.map((part) => {
                                        const isTarget =
                                            part.id === variables.partId;
                                        const sharesCanonical =
                                            renamedCanonicalId &&
                                            part.partId === renamedCanonicalId;
                                        if (!isTarget && !sharesCanonical)
                                            return part;
                                        return {
                                            ...part,
                                            ...(isTarget
                                                ? { name: variables.newName }
                                                : {}),
                                            part:
                                                sharesCanonical && part.part
                                                    ? {
                                                          ...part.part,
                                                          name: variables.newName,
                                                      }
                                                    : part.part,
                                        };
                                    }),
                                },
                            };
                        }

                        case "update-attribute":
                            // Find the target part
                            targetPart = findPartOrNull(
                                old.parts.present,
                                variables.partId,
                            );

                            if (!targetPart) {
                                return old;
                            }

                            // Handle top-level fields differently from attributes
                            if (topLevelFields.includes(variables.name)) {
                                // Update top-level field directly
                                updatedPart = {
                                    ...targetPart,
                                    [variables.name]: variables.content,
                                };
                            } else {
                                // Update attributes array
                                updatedPart = {
                                    ...targetPart,
                                    attributes:
                                        targetPart.attributes?.map((attr) =>
                                            attr.name === variables.name
                                                ? {
                                                      ...attr,
                                                      content:
                                                          variables.content,
                                                  }
                                                : attr,
                                        ) || [],
                                };
                            }

                            // Create the updated session data
                            updatedData = {
                                ...old,
                                parts: {
                                    ...old.parts,
                                    present: old.parts.present.map((part) =>
                                        part.id === variables.partId &&
                                        updatedPart
                                            ? updatedPart
                                            : part,
                                    ),
                                },
                            };

                            return updatedData;

                        case "update-session-attributes":
                            return {
                                ...old,
                                session: {
                                    ...old.session,
                                    attributes: variables.attributes,
                                },
                            };

                        default:
                            return old;
                    }
                },
            );

            return { previousData };
        },
        onError: (err, variables, context) => {
            // Revert the optimistic update on error
            if (context?.previousData) {
                logger
                    .withMetadata({
                        action: variables.action,
                        error: err.message,
                    })
                    .error("Mutation failed, reverting optimistic update");

                queryClient.setQueryData(
                    ["session", sessionId, sessionToken],
                    context.previousData,
                );
            }
            toast.error("Failed to update part");
        },
        onSuccess: (data, variables) => {
            if (data.success) {
                toast.success(data.message);

                // Track successful simple operations
                if (variables.action === "rename-interaction") {
                    trackEventWithAttribution("part renamed", {
                        session: sessionId,
                    });
                } else if (variables.action === "rename") {
                    // AIDEV-NOTE: Track rename of actual part entity
                    trackEventWithAttribution("part renamed", {
                        partId: variables.partId,
                    });
                } else if (variables.action === "delete") {
                    trackEventWithAttribution("part deleted", {
                        session: sessionId,
                    });
                } else if (variables.action === "update-attribute") {
                    trackEventWithAttribution("part attribute updated", {
                        session: sessionId,
                        partId: variables.partId,
                        attributeName: variables.name,
                    });
                }
            } else {
                logger
                    .withMetadata({
                        action: variables.action,
                        error: data.error,
                    })
                    .error("Mutation returned error");

                toast.error(data.error);
                return;
            }

            if (variables.action === "delete") {
                queryClient.setQueryData(
                    ["session", sessionId, sessionToken],
                    (old: {
                        session: TSession;
                        parts: { present: TPart[]; other: IdentifiedPart[] };
                    }) => {
                        if (!old) return old;
                        return {
                            ...old,
                            parts: {
                                ...old.parts,
                                present: old.parts.present.filter(
                                    (part) => part.id !== variables.partId,
                                ),
                            },
                        };
                    },
                );
                navigate(`/sessions/${sessionId}`, { replace: true });
                // For split and merge, we need to refetch the session data
            } else if (
                variables.action === "split" ||
                variables.action === "merge"
            ) {
                queryClient.invalidateQueries({
                    queryKey: ["session", sessionId, sessionToken],
                });
                navigate(`/sessions/${sessionId}`, { replace: true });
            } else if (variables.action === "update-attribute") {
                // For attribute updates, we just invalidate the session query to ensure we have the latest data
                queryClient.invalidateQueries({
                    queryKey: ["session", sessionId, sessionToken],
                });
            }
        },
    });
};

// Added imports for session summary types
type SessionSummaryType = "short" | "long";

// Use the already imported SessionMutationVariables type from '@/types'

/**
 * Unified hook for all session mutations (title, date, summaries)
 */
export const useSessionMutation = (
    sessionId: string,
    sessionOwnerId?: string,
    sessionToken?: string | null,
) => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    const { userId } = useSupabaseSession();

    // AIDEV-NOTE: Wrapper to track events for both session owner and recipient
    // Uses Record<string, unknown> to avoid complex generic inference issues with trackEvent
    const trackEventWithAttribution = useCallback(
        (event: AnalyticsEvent["name"], props: Record<string, unknown>) => {
            // Cast to satisfy trackEvent's generic signature
            const track = trackEvent as (
                e: string,
                p: Record<string, unknown>,
            ) => void;

            if (!sessionOwnerId) return track(event, props);

            track(event, props);

            const isSessionOwner = sessionOwnerId === userId;

            if (!isSessionOwner) {
                trackEventAsUser(
                    `recipient ${event}` as AnalyticsEvent["name"],
                    sessionOwnerId,
                    props,
                    apiClient,
                );
            }
        },
        [sessionOwnerId, userId, apiClient],
    );

    return useMutation({
        mutationFn: async (variables: SessionMutationVariables) => {
            // Create the appropriate payload and make the request based on action type
            const response = await (async () => {
                switch (variables.action) {
                    case "update-client":
                        return apiClient.post(`/session/${sessionId}/update`, {
                            clientId:
                                variables.clientId === "other"
                                    ? null
                                    : variables.clientId,
                        });

                    case "update-practitioner":
                        return apiClient.post(`/session/${sessionId}/update`, {
                            practitionerId: variables.practitionerId,
                        });

                    case "update-title":
                        return apiClient.post(`/session/${sessionId}/update`, {
                            title: variables.title,
                        });

                    case "update-date":
                        return apiClient.post(`/session/${sessionId}/update`, {
                            meetingStartTime: variables.date,
                        });

                    case "update-summary": {
                        // Convert newlines back to pipe characters for storage
                        const formattedContent = variables.content.replace(
                            /\n\n/g,
                            "|",
                        );

                        return apiClient.post(`/session/${sessionId}/update`, {
                            [variables.summaryType === "short"
                                ? "shortSummary"
                                : "longSummary"]: formattedContent,
                        });
                    }

                    default: {
                        const _exhaustiveCheck: never = variables;
                        throw new Error(
                            `Unsupported action type: ${
                                (variables as SessionMutationVariables).action
                            }`,
                        );
                    }
                }
            })();

            if (!response.ok) {
                const errorData = await response.json();
                // AIDEV-NOTE: Surface billing block as a distinct error so onError can show a specific message
                if (
                    response.status === 402 &&
                    errorData.code === "BILLING_LLM_BLOCKED"
                ) {
                    throw new Error("BILLING_BLOCKED");
                }
                throw new Error(errorData.error || "Failed to update session");
            }

            return variables;
        },
        onMutate: async (variables: SessionMutationVariables) => {
            await queryClient.cancelQueries({
                queryKey: ["session", sessionId, sessionToken],
            });
            const previousData = queryClient.getQueryData([
                "session",
                sessionId,
                sessionToken,
            ]) as {
                session: TSession;
                parts: { present: TPart[]; other: IdentifiedPart[] };
            } | null;

            queryClient.setQueryData(
                ["session", sessionId, sessionToken],
                (old: GetSessionResult) => {
                    if (!old) return old;

                    // Apply optimistic updates based on action type
                    switch (variables.action) {
                        case "update-client":
                            logger
                                .withMetadata({ variables })
                                .debug("update-client");
                            return {
                                ...old,
                                session: {
                                    ...old.session,
                                    clientId: variables.clientId,
                                    client: variables.clientId
                                        ? {
                                              ...old.session?.client,
                                              id: variables.clientId,
                                              email: variables.clientEmail,
                                          }
                                        : undefined,
                                },
                            };
                        case "update-practitioner":
                            logger
                                .withMetadata({ variables })
                                .debug("update-practitioner");
                            return {
                                ...old,
                                session: {
                                    ...old.session,
                                    practitionerId: variables.practitionerId,
                                    practitioner: variables.practitionerId
                                        ? {
                                              ...old.session?.practitioner,
                                              id: variables.practitionerId,
                                              email: variables.practitionerEmail,
                                          }
                                        : undefined,
                                },
                            };
                        case "update-title":
                            return {
                                ...old,
                                session: {
                                    ...old.session,
                                    title: variables.title,
                                },
                            };

                        case "update-date":
                            return {
                                ...old,
                                session: {
                                    ...old.session,
                                    meetingStartTime:
                                        variables.date.toISOString(),
                                },
                            };

                        case "update-summary": {
                            // Create or update the rawReport object

                            return {
                                ...old,
                                session: {
                                    ...old.session,
                                    [variables.summaryType === "short"
                                        ? "shortSummary"
                                        : "longSummary"]: variables.content,
                                },
                            };
                        }

                        default: {
                            const _exhaustiveCheck: never = variables;
                            return old;
                        }
                    }
                },
            );

            return { previousData };
        },
        onError: (err, variables: SessionMutationVariables, context) => {
            queryClient.setQueryData(
                ["session", sessionId, sessionToken],
                context?.previousData,
            );

            // Handle errors based on action type
            switch (variables.action) {
                case "update-title":
                    toast.error("Failed to update title");
                    break;

                case "update-date":
                    toast.error("Failed to update date");
                    break;

                case "update-summary":
                    toast.error(
                        `Failed to update ${variables.summaryType} summary`,
                    );
                    break;

                case "update-client":
                    toast.error("Failed to update client");
                    break;

                case "update-practitioner":
                    if (
                        err instanceof Error &&
                        err.message === "BILLING_BLOCKED"
                    ) {
                        toast.error(
                            "Your practitioner has reached their session limit for this period. Your session has been saved and can be assigned once they upgrade.",
                        );
                    } else {
                        toast.error("Failed to update practitioner");
                    }
                    break;

                default: {
                    const _exhaustiveCheck: never = variables;
                    toast.error("Failed to update session");
                }
            }

            logger
                .withError(err as Error)
                .error(`Error updating session ${sessionId}`);
        },
        onSuccess: (
            data: SessionMutationVariables,
            variables: SessionMutationVariables,
        ) => {
            // Handle success based on action type
            switch (variables.action) {
                case "update-title":
                    toast.success("Title updated successfully");
                    break;

                case "update-date":
                    toast.success("Date updated successfully");
                    break;

                case "update-summary":
                    if (sessionOwnerId) {
                        // Track the successful summary update
                        trackEventWithAttribution("session summary updated", {
                            session: sessionId,
                            type: variables.summaryType,
                            characterCount: variables.content.length,
                        });
                    }

                    toast.success(
                        `${
                            variables.summaryType === "short" ? "Short" : "Long"
                        } summary updated successfully`,
                    );
                    break;

                case "update-client":
                    toast.success("Client updated successfully");
                    break;

                case "update-practitioner":
                    toast.success("Practitioner updated successfully");
                    break;

                default: {
                    const _exhaustiveCheck: never = variables;
                }
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ["session", sessionId, sessionToken],
            });
        },
    });
};

// Target part can return undefined from find, but we want to use null for absence
const findPartOrNull = (
    partInteractions: TPartInteractionWithImage[],
    partId: string,
): TPartInteractionWithImage | null =>
    partInteractions.find((partInteraction) => partInteraction.id === partId) ||
    null;

// AIDEV-NOTE: Clear session cache helpers
export const useClearSessionCache = (sessionId?: string) => {
    const queryClient = useQueryClient();
    return useCallback(() => {
        if (sessionId) {
            queryClient.removeQueries({
                queryKey: ["session", sessionId],
                exact: false,
            });
            queryClient.removeQueries({
                queryKey: ["sessionStatus", sessionId],
                exact: false,
            });
        } else {
            queryClient.removeQueries({ queryKey: ["session"], exact: false });
            queryClient.removeQueries({
                queryKey: ["sessionStatus"],
                exact: false,
            });
        }
    }, [queryClient, sessionId]);
};
